import { lazy, ReactElement, Suspense } from "react";
import { RouteObject } from "react-router-dom";
import { App } from "./App";
import { Protected } from "./components/Protected";
import { AppContextProvider } from "./contexts/AppContext";
import { BfMapId } from "./lib/api/models";
import { ErrorView } from "./views/errors/ErrorView";
import { RoutingErrorView } from "./views/errors/RoutingErrorView";
import { Loading } from "./views/Loading";

const Mappy = lazy(() => import("./components/map/Mappy"));
const HomeView = lazy(() => import("./views/HomeView"));
const EventView = lazy(() => import("./views/EventView"));
const FeedbackView = lazy(() => import("./views/FeedbackView"));
const LoginView = lazy(() => import("./views/LoginView"));
const AuthoriseView = lazy(() => import("./views/AuthoriseView"));
const SupportedDevicesView = lazy(() => import("./views/SupportedDevicesView"));
const WelcomeView = lazy(() => import("./views/WelcomeView"));
const ContactUsView = lazy(() => import("./views/ContactView"));

const AboutView = lazy(() => import("./views/AboutView"));
const LegendView = lazy(() => import("./views/SymbolLegendView"));
const AccountView = lazy(() => import("./views/AccountView"));
const DiagnosticsView = lazy(() => import("./views/DiagnosticsView"));
const ProView = lazy(() => import("./views/plans/ProView"));
const BusinessView = lazy(() => import("./views/plans/BusinessView"));
const EnterpriseView = lazy(() => import("./views/plans/EnterpriseView"));
const CancelView = lazy(() => import("./views/plans/CancelView"));
const PlansView = lazy(() => import("./views/plans/PlansView"));
const DeleteAccountView = lazy(() => import("./views/DeleteAccountView"));
const DevelopersView = lazy(() => import("./views/DevelopersView"));
const WaitingListView = lazy(() => import("./views/WaitingListView"));

const NotificationsSearchView = lazy(() => import("./views/notifications/SearchView"));
const NotificationsListView = lazy(() => import("./views/notifications/InboxView"));
const NotificationsEnableView = lazy(() => import("./views/notifications/EnableView"));
const NotificationsSetupView = lazy(() => import("./views/notifications/ConfigView"));
const NotificationsSettingsView = lazy(() => import("./views/notifications/SettingsView"));

const ManageListView = lazy(() => import("./views/management/ListView"));
const ManageDataSourceView = lazy(() => import("./views/management/DataSourceView"));
const ManageEventView = lazy(() => import("./views/management/EventView"));
const ManageStartEventView = lazy(() => import("./views/management/StartEventView"));
const ManageEventPreviewView = lazy(() => import("./views/management/EventPreviewView"));

const CustomLayerListView = lazy(() => import("./views/customlayers/ListView"));
const CustomLayerSettingsView = lazy(() => import("./views/customlayers/SettingsView"));
const CustomLayerSummaryView = lazy(() => import("./views/customlayers/SummaryView"));
const CustomLayerEditView = lazy(() => import("./views/customlayers/EditView"));
const CustomLayerNotifications = lazy(() => import("./views/customlayers/NotificationsView"));
const CopyNotification = lazy(() => import("./views/customlayers/CopyNotification"));

const LicencesListView = lazy(() => import("./views/licencing/ListView"));
const LicencesCreateView = lazy(() => import("./views/licencing/CreateView"));
const LicencesManageView = lazy(() => import("./views/licencing/ManageView"));
const UserDiagnosticsView = lazy(() => import("./views/ManageUserView"));
const StreamView = lazy(() => import("./views/StreamView"));

const CreateOrgView = lazy(() => import("./views/org/CreateOrgView"));
const ManageOrgView = lazy(() => import("./views/org/ManageOrgView"));
const OrgUsersView = lazy(() => import("./views/org/OrgUsersView"));
const CreateOrgUser = lazy(() => import("./views/org/CreateOrgUser"));
const CreateOrgUserLicence = lazy(() => import("./views/org/CreateOrgUserLicence"));

const ModerationQueueView = lazy(() => import("./views/media/ModerationQueue"));

export interface RouteDef {
    paths: string[];
    element: ReactElement;
    routeType: RouteType;
}

export enum RouteType {
    /// Fixed = simple route, no lazy loading, no auth
    StaticPublic,

    /// Dynamic = lazy loaded, no auth
    LazyLoaded,

    /// Protected = lazy loaded, requires auth
    ProtectedLazy,
}

export enum NamedRoute {
    Root,
    Welcome,
    About,
    Login,
    Legend,
    SupportedDevices,
    Error,
    Feedback,
    ContactUs,
    Account,
    Diagnostics,
    Pro,
    Enterprise,
    Cancel,
    Plans,
    DeleteAccount,
    Developers,

    NotificationsSearch,
    NotificationsList,
    NotificationsEnable,
    NotificationsSetup,

    ListDataSources,
    ManageDataSource,
    ManageEvent,
    StartManagingEvent,
    EventPreview,

    CustomLayersList,
    CustomLayersSettings,
    CustomLayersNotifications,
    CustomLayersEdit,
    CustomLayerCopyNotification,
    LicencesList,
    LicencesCreate,
    LicencesManage,

    UserDiagnostics,
    Business,
    WaitList,
    Stream,
    NotificationsSettings,
    OrgCreate,
    OrgSettings,
    OrgUsers,
    CustomLayersSummary,
    CreateOrgUser,
    CreateOrgUserLicence,

    Event,
    Loading,
    ModerationQueue,
    Authorise,
}

export const ROUTE_MAP: Record<NamedRoute, RouteDef> = {
    [NamedRoute.Root]: {
        paths: ["/", "/ux/"],
        element: <HomeView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Welcome]: {
        paths: ["/ux/welcome"],
        element: <WelcomeView />,
        routeType: RouteType.LazyLoaded,
    },

    [NamedRoute.Login]: {
        paths: ["/ux/login", "/login"],
        element: <LoginView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Authorise]: {
        paths: ["/ux/authorise"],
        element: <AuthoriseView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.SupportedDevices]: {
        paths: ["/ux/supported"],
        element: <SupportedDevicesView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Error]: {
        paths: ["/ux/error", "/error"],
        element: <ErrorView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Feedback]: {
        paths: ["/ux/feedback"],
        element: <FeedbackView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.ContactUs]: {
        paths: ["/ux/contact"],
        element: <ContactUsView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.About]: {
        paths: ["/ux/about"],
        element: <AboutView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Legend]: {
        paths: ["/ux/legend"],
        element: <LegendView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Pro]: {
        paths: ["/ux/plans/pro"],
        element: <ProView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Plans]: {
        paths: ["/ux/plans"],
        element: <PlansView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Diagnostics]: {
        paths: ["/ux/diag"],
        element: <DiagnosticsView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Business]: {
        paths: ["/ux/plans/business"],
        element: <BusinessView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.Enterprise]: {
        paths: ["/ux/plans/enterprise"],
        element: <EnterpriseView />,
        routeType: RouteType.LazyLoaded,
    },
    [NamedRoute.WaitList]: {
        paths: ["/ux/waitlist"],
        element: <WaitingListView />,
        routeType: RouteType.LazyLoaded,
    },

    [NamedRoute.Account]: {
        paths: ["/ux/account"],
        element: (
            <Mappy mapId={BfMapId.main}>
                <AccountView />
            </Mappy>
        ),
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.NotificationsSearch]: {
        paths: ["/ux/notifications/search"],
        element: <NotificationsSearchView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.NotificationsList]: {
        paths: ["/ux/notifications"],
        element: <NotificationsListView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.NotificationsEnable]: {
        paths: ["/ux/notifications/enable"],
        element: <NotificationsEnableView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.Cancel]: {
        paths: ["/ux/plans/cancel"],
        element: <CancelView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.LicencesList]: {
        paths: ["/ux/licencing"],
        element: <LicencesListView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.DeleteAccount]: {
        paths: ["/ux/account/delete"],
        element: <DeleteAccountView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.Developers]: {
        paths: ["/ux/account/developers"],
        element: <DevelopersView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.NotificationsSetup]: {
        paths: ["/ux/notifications/setup"],
        element: <NotificationsSetupView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.ListDataSources]: {
        paths: ["/ux/management/sources"],
        element: <ManageListView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.ManageDataSource]: {
        paths: ["/ux/management/source/:sourceId"],
        element: <ManageDataSourceView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.ManageEvent]: {
        paths: ["/ux/management/event/:eKey"],
        element: <ManageEventView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.StartManagingEvent]: {
        paths: ["/ux/management/start/:sourceId/:eKey"],
        element: <ManageStartEventView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.EventPreview]: {
        paths: ["/ux/management/preview/:sourceId/:eKey"],
        element: <ManageEventPreviewView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.CustomLayersList]: {
        paths: ["/ux/customlayers"],
        element: <CustomLayerListView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.CustomLayerCopyNotification]: {
        paths: ["/ux/customlayer/copy/:customLayerId/:notificationId"],
        element: <CopyNotification />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.CustomLayersSettings]: {
        paths: ["/ux/customlayer/settings/:customLayerId"],
        element: <CustomLayerSettingsView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.CustomLayersSummary]: {
        paths: ["/ux/customlayer/:customLayerId"],
        element: <CustomLayerSummaryView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.CustomLayersNotifications]: {
        paths: [
            "/ux/customlayer/notifications/:customLayerId",
            "/ux/customlayer/notifications/:customLayerId/:notificationId",
        ],
        element: <CustomLayerNotifications />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.LicencesCreate]: {
        paths: ["/ux/licencing/create"],
        element: <LicencesCreateView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.LicencesManage]: {
        paths: ["/ux/licencing/:licenceId"],
        element: <LicencesManageView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.CustomLayersEdit]: {
        paths: ["/ux/customlayer/map/:customLayerId"],
        element: <CustomLayerEditView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.UserDiagnostics]: {
        paths: ["/ux/user/:userId"],
        element: <UserDiagnosticsView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.Stream]: {
        paths: ["/ux/stream"],
        element: <StreamView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.NotificationsSettings]: {
        paths: ["/ux/notifications/settings"],
        element: <NotificationsSettingsView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.OrgCreate]: {
        paths: ["/ux/org/create"],
        element: <CreateOrgView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.OrgSettings]: {
        paths: ["/ux/org/settings"],
        element: <ManageOrgView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.OrgUsers]: {
        paths: ["/ux/org/users"],
        element: <OrgUsersView />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.CreateOrgUser]: {
        paths: ["/ux/org/user/create"],
        element: <CreateOrgUser />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.Event]: {
        paths: ["/ux/event/:eventId"],
        element: <EventView />,
        routeType: RouteType.StaticPublic,
    },
    [NamedRoute.Loading]: {
        paths: ["/ux/loading"],
        element: <Loading />,
        routeType: RouteType.StaticPublic,
    },
    [NamedRoute.CreateOrgUserLicence]: {
        paths: ["/ux/org/create/root"],
        element: <CreateOrgUserLicence />,
        routeType: RouteType.ProtectedLazy,
    },
    [NamedRoute.ModerationQueue]: {
        paths: ["/ux/moderation/queue"],
        element: <ModerationQueueView />,
        routeType: RouteType.ProtectedLazy,
    },
};

export class RouteMap {
    static Path(
        namedRoute: NamedRoute,
        arg1: string | null = null,
        arg2: string | null = null
    ): string {
        let route = ROUTE_MAP[namedRoute].paths[0].split("/:")[0];

        if (arg1) {
            route = route + "/" + arg1;
        }

        if (arg2) {
            route = route + "/" + arg2;
        }

        return route;
    }
}

// Build children by iterating over the ROUTE_MAP and creating a RouteObject for each
const children: RouteObject[] = [];

for (const route of Object.values(ROUTE_MAP)) {
    if (route.paths.length === 0) {
        continue;
    }
    for (const path of route.paths) {
        let element: ReactElement;
        if (route.routeType === RouteType.LazyLoaded) {
            element = <Suspense fallback={<Loading />}>{route.element}</Suspense>;
        } else if (route.routeType === RouteType.ProtectedLazy) {
            element = (
                <Suspense fallback={<Loading />}>
                    <Protected>{route.element}</Protected>
                </Suspense>
            );
        } else {
            element = route.element;
        }

        children.push({
            // Path, with leading / removed
            path: path.replace("/", ""),
            element,
        });
    }
}

export const Routes: RouteObject[] = [
    {
        path: "/",
        element: (
            <AppContextProvider>
                <App />
            </AppContextProvider>
        ),
        errorElement: (
            <AppContextProvider>
                <RoutingErrorView />
            </AppContextProvider>
        ),
        children,
    },
];
